import { assign } from 'xstate5';

import {
  type FetchOrdersOutput,
  type OrdersMachineAction,
  type OrderWithIdAndWantedTime,
} from '../orders-machine.types';
import {
  ordersMachineLogger,
  sortOrdersByWantedTime,
  withoutDuplicateOrders,
} from '../orders-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

export function createOrdersMachineActions<Order extends OrderWithIdAndWantedTime>() {
  type Action<ActionParams extends Record<string, unknown> | void = void> = OrdersMachineAction<
    Order,
    ActionParams
  >;

  /**
   * Stores the resolved orders in the context.
   */
  const onFetchOrdersDone: Action<FetchOrdersOutput<Order>> = assign(({ context }, params) => {
    const updatedOrders = [...context.orders, ...params.orders];

    return {
      orders: sortOrdersByWantedTime(withoutDuplicateOrders(updatedOrders)),
      canFetchMoreOrders: context.page < params.lastPage,
    };
  });

  const logFetchOrdersError: Action<{ error: unknown }> = (_, params) => {
    ordersMachineLogger.error(params.error);
  };

  const increasePage: Action = assign(({ context }) => ({
    page: context.page + 1,
  }));

  /**
   * Resets the context of the machine.
   *
   * Can be used to clear the state before restarting the machine.
   */
  const resetContext: Action = assign(() => ({
    orders: [],
    page: 1,
    canFetchMoreOrders: false,
  }));

  return {
    onFetchOrdersDone,
    logFetchOrdersError,
    increasePage,
    resetContext,
  };
}
